import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '../ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Checkbox } from '../ui/checkbox'
import { EntityPicture } from '../EntityPicture'
import { OrganizationStatusBadge } from './OrganizationStatusBadge'
import { useOrganizationDetailUnified } from '../../hooks/useQueries'
import { useUpdateOrganization } from '../../api/queries'
import type { OrganizationAction } from './OrganizationActionDialog'

interface Props {
  orgCode: string | undefined
  isAdmin: boolean
  onClose: () => void
  onAction: (action: OrganizationAction) => void
}

export function OrganizationEditDrawer({ orgCode, isAdmin, onClose, onAction }: Props) {
  const { t, i18n } = useTranslation('admin')
  const queryClient = useQueryClient()
  const isOpen = Boolean(orgCode)
  const { org, isLoading } = useOrganizationDetailUnified(orgCode, isAdmin)

  const [name, setName] = useState('')
  const [isTemplate, setIsTemplate] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [discardOpen, setDiscardOpen] = useState(false)

  // Sync local form state once per loaded org — avoids overwriting in-flight
  // edits when the underlying query refetches.
  const lastUidRef = useRef<string | null>(null)
  useEffect(() => {
    if (org && org.uid !== lastUidRef.current) {
      lastUidRef.current = org.uid
      setName(org.name || '')
      setIsTemplate(org.is_template || false)
      setSaveError(null)
    }
  }, [org])

  const dirty = Boolean(org && (name !== (org.name || '') || isTemplate !== (org.is_template || false)))

  const update = useUpdateOrganization(org?.code ?? '', {
    onSuccess: () => {
      setSaveError(null)
      queryClient.invalidateQueries({ predicate: (q) => Array.isArray(q.queryKey) && (q.queryKey[1] === '/api/v1/organizations' || q.queryKey[1] === '/api/v1/admin/organizations') })
      onClose()
    },
    onError: (err: any) => {
      // The mutation serialises the typed error body into `message`
      // (spec 2026-09-09-10) — show its title, never the raw JSON.
      let title: string | undefined = err?.title
      if (!title && typeof err?.message === 'string') {
        try {
          const parsed = JSON.parse(err.message)
          title = parsed?.title || parsed?.detail
        } catch {
          title = err.message
        }
      }
      setSaveError(title || t('organizations.drawer.edit_save_failed'))
    },
  })

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      setSaveError(t('organizations.drawer.field_name_required'))
      return
    }
    update.mutate({ name: name.trim(), is_template: isTemplate })
  }

  const handleClose = () => {
    if (dirty) {
      setDiscardOpen(true)
    } else {
      onClose()
    }
  }

  const formatDate = (iso?: string | null) => {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleString(i18n.language) } catch { return iso }
  }

  return (
    <Sheet open={isOpen} onOpenChange={(v) => { if (!v) handleClose() }}>
      <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col" data-testid="org-edit-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {org?.name || org?.code || '…'}
            {isAdmin && org?.status && <OrganizationStatusBadge status={org.status} />}
          </SheetTitle>
          <SheetDescription>{org?.code}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-6">
          {isLoading ? (
            <div className="text-sm text-muted-foreground">{t('organizations.loading')}</div>
          ) : org ? (
            <>
              {/* Edit form */}
              <section>
                <h3 className="text-sm font-medium mb-3">{t('organizations.drawer.edit_form_title')}</h3>
                <form onSubmit={handleSave} className="space-y-4" data-testid="org-edit-form">
                  <div className="space-y-2">
                    <Label>{t('organizations.drawer.field_picture')}</Label>
                    <p className="text-xs text-muted-foreground">{t('organizations.drawer.field_picture_help')}</p>
                    <EntityPicture
                      entityType="organization"
                      entityId={org.uid}
                      documentUid={org.picture?.document_uid}
                      orgCode={org.code}
                      size="128x128"
                      editable={true}
                      className="w-32 h-32"
                      alt={`${org.name} picture`}
                      onUpload={(success, error) => {
                        if (!success) setSaveError(error || t('organizations.drawer.edit_save_failed'))
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="org-name">
                      {t('organizations.drawer.field_name')} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="org-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={update.isPending}
                      data-testid="org-name-input"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="org-code">{t('organizations.drawer.field_code')}</Label>
                    <Input id="org-code" value={org.code} disabled className="bg-muted cursor-not-allowed" data-testid="org-code-input" />
                    <p className="text-xs text-muted-foreground">{t('organizations.drawer.field_code_help')}</p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="is-template"
                      checked={isTemplate}
                      onCheckedChange={(checked) => setIsTemplate(checked === true)}
                      disabled={update.isPending}
                      data-testid="is-template-checkbox"
                    />
                    <Label htmlFor="is-template" className="cursor-pointer">{t('organizations.drawer.field_is_template')}</Label>
                  </div>

                  {saveError && (
                    <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3" data-testid="org-edit-error">
                      <p className="text-sm text-destructive">{saveError}</p>
                    </div>
                  )}

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={handleClose} disabled={update.isPending} data-testid="org-cancel-button">
                      {t('organizations.action.cancel')}
                    </Button>
                    <Button type="submit" size="sm" disabled={update.isPending || !dirty} data-testid="org-save-button">
                      {update.isPending ? t('organizations.action.saving') : t('organizations.action.save')}
                    </Button>
                  </div>
                </form>
              </section>

              {isAdmin && (
                <>
                  <section>
                    <h3 className="text-sm font-medium mb-2">{t('organizations.drawer.identity')}</h3>
                    <dl className="text-sm grid grid-cols-2 gap-y-1">
                      <dt className="text-muted-foreground">{t('organizations.column.code')}</dt>
                      <dd><code>{org.code}</code></dd>
                      <dt className="text-muted-foreground">{t('organizations.column.name')}</dt>
                      <dd>{org.name || '—'}</dd>
                      <dt className="text-muted-foreground">UID</dt>
                      <dd className="font-mono text-xs">{org.uid}</dd>
                    </dl>
                  </section>

                  <section>
                    <h3 className="text-sm font-medium mb-2">{t('organizations.drawer.timeline')}</h3>
                    <dl className="text-sm grid grid-cols-2 gap-y-1">
                      <dt className="text-muted-foreground">{t('organizations.drawer.created')}</dt>
                      <dd>{formatDate(org.created_at)}</dd>
                      <dt className="text-muted-foreground">{t('organizations.drawer.updated')}</dt>
                      <dd>{formatDate(org.updated_at)}</dd>
                      {org.suspended_at && (
                        <>
                          <dt className="text-muted-foreground">{t('organizations.drawer.suspended_at')}</dt>
                          <dd>{formatDate(org.suspended_at)}</dd>
                        </>
                      )}
                      {org.deleted_at && (
                        <>
                          <dt className="text-muted-foreground">{t('organizations.drawer.deleted_at')}</dt>
                          <dd>{formatDate(org.deleted_at)}</dd>
                        </>
                      )}
                    </dl>
                  </section>

                  <section>
                    <h3 className="text-sm font-medium mb-2">{t('organizations.drawer.stats')}</h3>
                    <div className="grid grid-cols-4 gap-2">
                      {(['members', 'assets', 'documents', 'issues'] as const).map((k) => (
                        <div key={k} className="border rounded-md p-2 text-center">
                          <div className="text-xs text-muted-foreground">{k}</div>
                          <div className="text-lg font-semibold" data-testid={`org-count-${k}`}>
                            {org.counts?.[k] ?? 0}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section>
                    <h3 className="text-sm font-medium mb-2">{t('organizations.drawer.actions')}</h3>
                    <div className="flex flex-wrap gap-2">
                      {org.suspended_at ? (
                        <Button variant="outline" size="sm" onClick={() => onAction('unsuspend')} data-testid="org-action-unsuspend">
                          {t('organizations.action.unsuspend')}
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => onAction('suspend')} disabled={Boolean(org.deleted_at)} data-testid="org-action-suspend">
                          {t('organizations.action.suspend')}
                        </Button>
                      )}
                      {org.deleted_at ? (
                        <Button variant="outline" size="sm" onClick={() => onAction('restore')} data-testid="org-action-restore">
                          {t('organizations.action.restore')}
                        </Button>
                      ) : (
                        <Button variant="destructive" size="sm" onClick={() => onAction('delete')} disabled={org.is_template} data-testid="org-action-delete">
                          {t('organizations.action.delete')}
                        </Button>
                      )}
                    </div>
                  </section>
                </>
              )}
            </>
          ) : null}
        </div>

        <SheetFooter>
          <Button variant="outline" size="sm" onClick={handleClose} data-testid="org-edit-drawer-close">
            {t('organizations.drawer.close')}
          </Button>
        </SheetFooter>
      </SheetContent>

      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('organizations.drawer.unsaved_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('organizations.drawer.unsaved_body')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="discard-keep">{t('organizations.drawer.unsaved_keep')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setDiscardOpen(false); onClose() }}
              data-testid="discard-confirm"
            >
              {t('organizations.drawer.unsaved_discard')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  )
}
